import { payRequestReferencesZodSchema } from "@@/shared/pay-requests/events/_pay-request-references";
import { payRequestIdZodSchema } from "@@/shared/pay-requests/pay-request-id";
import { priceZodSchema } from "@@/shared/prices/price";
import { translatableZodSchema } from "@@/shared/translations/translation";
import type { Except } from "type-fest";
import type { ZodSchema} from "zod";
import { z } from "zod";
import { signatureFactory } from "../../signature-factory";
import type { _OrderEventBaseFactoryParams } from "../_order-base.event";
import {
    _orderEventBaseFactory,
    _orderEventBaseZodObject,
} from "../_order-base.event";

// Event data
const _orderPayRequestCancelledEventData = z.object({
    type: z.literal("PAY_REQUEST_CANCELLED"),
    payRequestId: payRequestIdZodSchema,
    reason: translatableZodSchema,
    /** Total pay request amount */
    price: priceZodSchema,
    /**
     * An object with references to the order, order group, order events and provider that the
     * pay request is a payment for
     * */
    paymentFor: payRequestReferencesZodSchema,
    replacedBy: payRequestIdZodSchema.optional(),
});
type _OrderPayRequestCancelledEventData = z.infer<
    typeof _orderPayRequestCancelledEventData
>;

// Full event merged with event base data
const orderPayRequestCancelledEventZodObject = _orderEventBaseZodObject.merge(
    _orderPayRequestCancelledEventData,
);

type OrderPayRequestCancelledEvent = z.infer<
    typeof orderPayRequestCancelledEventZodObject
>;
const orderPayRequestCancelledEventZodSchema: ZodSchema<OrderPayRequestCancelledEvent> =
    orderPayRequestCancelledEventZodObject;

// Factory params
type OrderPayRequestCancelledEventFactoryParams = Except<
    _OrderEventBaseFactoryParams &
        Except<_OrderPayRequestCancelledEventData, "type">,
    "signature"
>;

// Factory
function orderPayRequestCancelledEventFactory(
    params: OrderPayRequestCancelledEventFactoryParams,
): OrderPayRequestCancelledEvent {
    const result: OrderPayRequestCancelledEvent = {
        //TODO @EMANUEL is this a correct signature?
        ..._orderEventBaseFactory({
            ...params,
            signature: signatureFactory(
                params.orderId,
                "PAY_REQUEST_CANCELLED",
                [params.payRequestId],
            ),
        }),
        type: "PAY_REQUEST_CANCELLED",
        payRequestId: params.payRequestId,
        reason: params.reason,
        paymentFor: params.paymentFor,
        price: params.price,
        replacedBy: params.replacedBy || undefined,
    };

    return result;
}

const isOrderPayRequestCancelledEvent = (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    event: any,
): event is OrderPayRequestCancelledEvent => {
    return (
        event._type === "ORDER_EVENT" && event?.type === "PAY_REQUEST_CANCELLED"
    );
};
export {
    isOrderPayRequestCancelledEvent,
    orderPayRequestCancelledEventFactory,
    orderPayRequestCancelledEventZodObject,
    orderPayRequestCancelledEventZodSchema,
};
export type { OrderPayRequestCancelledEvent };
